import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ImagePlus, Loader2, Store, X } from "lucide-react";
import { api, apiError, mediaUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const BUSINESS_TYPES = [
  { id: "particulier", label: "Particulier / commerçant individuel" },
  { id: "societe", label: "Société (SARL, SA, GIE…)" },
];

export default function VendorApply() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({
    shop_name: "",
    phone: user?.phone || "",
    address: "",
    description: "",
    business_type: "particulier",
    tax_id: "",
    trade_register: "",
    categories: [],
    id_document: null,
    shop_photo: null,
  });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  const upload = async (field, file) => {
    setUploading(field);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, [field]: data.url }));
      toast.success("Document ajouté");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setUploading("");
    }
  };

  const toggleCategory = (name) =>
    setForm((f) => ({
      ...f,
      categories: f.categories.includes(name) ? f.categories.filter((c) => c !== name) : [...f.categories, name],
    }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/vendor/apply", form);
      setDone(true);
      toast.success("Dossier envoyé à l'administrateur");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (done)
    return (
      <div className="mx-auto max-w-lg px-4 py-20 text-center ds-rise" data-testid="vendor-apply-success">
        <Store className="mx-auto h-12 w-12 text-brand" />
        <h1 className="mt-4 font-display text-2xl font-bold text-slate-900">Dossier envoyé</h1>
        <p className="mt-2 text-sm text-slate-600">
          L'administrateur EMPIRE-SHOP va examiner votre dossier professionnel. Vous recevrez une notification dès la décision.
        </p>
        <div className="mt-5 flex justify-center gap-2">
          <Link to="/vendeur" className="ds-btn-primary">Voir mon espace vendeur</Link>
          <Link to="/" className="ds-btn-ghost">Retour à la boutique</Link>
        </div>
      </div>
    );

  return (
    <div className="mx-auto max-w-2xl px-4 py-6" data-testid="vendor-apply-page">
      <h1 className="font-display text-2xl font-bold text-slate-900 sm:text-3xl">Dossier vendeur professionnel</h1>
      <p className="mt-2 text-sm text-slate-600">
        Vendez vos produits sur EMPIRE-SHOP. Après validation de votre dossier par l'administrateur, vous pourrez publier
        vos articles, suivre vos ventes, vos commissions et votre solde.{" "}
        <Link to="/professionnels" className="font-semibold text-brand hover:underline">En savoir plus</Link>
      </p>

      <form onSubmit={submit} className="ds-card mt-5 space-y-4 p-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="ds-label">Nom de la boutique *</label>
            <input required className="ds-input mt-1" value={form.shop_name} onChange={(e) => setForm({ ...form, shop_name: e.target.value })} data-testid="vendor-apply-shop-name" />
          </div>
          <div>
            <label className="ds-label">Téléphone *</label>
            <input required className="ds-input mt-1" placeholder="+224 6XX XX XX XX" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="vendor-apply-phone" />
          </div>
          <div>
            <label className="ds-label">Type d'activité *</label>
            <select
              className="ds-input mt-1"
              value={form.business_type}
              onChange={(e) => setForm({ ...form, business_type: e.target.value })}
              data-testid="vendor-apply-business-type"
            >
              {BUSINESS_TYPES.map((b) => (
                <option key={b.id} value={b.id}>{b.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="ds-label">Adresse / localisation *</label>
            <input required className="ds-input mt-1" placeholder="Quartier, commune" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="vendor-apply-address" />
          </div>
          <div>
            <label className="ds-label">NIF (numéro d'identification fiscale)</label>
            <input className="ds-input mt-1" value={form.tax_id} onChange={(e) => setForm({ ...form, tax_id: e.target.value })} data-testid="vendor-apply-tax-id" />
          </div>
          <div>
            <label className="ds-label">
              {form.business_type === "societe" ? "Registre de commerce (RCCM) *" : "Registre de commerce (si vous en avez)"}
            </label>
            <input
              required={form.business_type === "societe"}
              className="ds-input mt-1"
              value={form.trade_register}
              onChange={(e) => setForm({ ...form, trade_register: e.target.value })}
              data-testid="vendor-apply-trade-register"
            />
          </div>
        </div>

        <div>
          <label className="ds-label">Catégories que vous vendez</label>
          <div className="mt-2 flex flex-wrap gap-2">
            {categories.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => toggleCategory(c.name)}
                className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                  form.categories.includes(c.name)
                    ? "border-brand bg-brand text-white"
                    : "border-slate-300 bg-white text-slate-600 hover:border-brand"
                }`}
                data-testid={`vendor-apply-category-${c.name}`}
              >
                {c.name}
              </button>
            ))}
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <Upload
            label="Pièce d'identité (optionnel)"
            value={form.id_document}
            busy={uploading === "id_document"}
            onFile={(f) => upload("id_document", f)}
            onClear={() => setForm({ ...form, id_document: null })}
            testid="vendor-apply-id-document"
          />
          <Upload
            label="Photo de la boutique (optionnel)"
            value={form.shop_photo}
            busy={uploading === "shop_photo"}
            onFile={(f) => upload("shop_photo", f)}
            onClear={() => setForm({ ...form, shop_photo: null })}
            testid="vendor-apply-shop-photo"
          />
        </div>

        <div>
          <label className="ds-label">Que vendez-vous ?</label>
          <textarea rows={3} className="ds-input mt-1" placeholder="Marques, volumes, capacité de stock…" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="vendor-apply-description" />
        </div>

        {error && <p className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700" data-testid="vendor-apply-error">{error}</p>}
        <button type="submit" disabled={busy} className="ds-btn-primary w-full" data-testid="vendor-apply-submit">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Envoyer mon dossier
        </button>
        <button type="button" onClick={() => navigate(-1)} className="ds-btn-ghost w-full">Annuler</button>
      </form>
    </div>
  );
}

const Upload = ({ label, value, busy, onFile, onClear, testid }) => (
  <div>
    <label className="ds-label">{label}</label>
    {value ? (
      <div className="relative mt-1 h-28 w-full overflow-hidden rounded-xl border border-slate-200">
        <img src={mediaUrl(value)} alt="" className="h-full w-full object-cover" />
        <button
          type="button"
          onClick={onClear}
          className="absolute right-2 top-2 grid h-6 w-6 place-items-center rounded-full bg-rose-600 text-white"
          data-testid={`${testid}-clear`}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    ) : (
      <label className="mt-1 flex h-28 cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-slate-300 text-xs text-slate-500 hover:border-brand">
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-5 w-5" />}
        {busy ? "Téléversement…" : "Ajouter une photo"}
        <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])} data-testid={testid} />
      </label>
    )}
  </div>
);
